import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Grid, Card, CardMedia, CardContent, CardActions,
  CircularProgress, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Fab, Alert, Snackbar, Chip, FormControl, 
  InputLabel, Select, MenuItem, Switch, FormControlLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LinkIcon from '@mui/icons-material/Link';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LanguageIcon from '@mui/icons-material/Language';
import ReorderIcon from '@mui/icons-material/Reorder';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api from './api';
import AddHandbookForm from './AddHandbookForm';
import EnhancedHandbookForm from './EnhancedHandbookForm';
import { AVAILABLE_LANGUAGES, DEFAULT_LANGUAGE } from './utils/languages';
import { getContentPreview, getWebDisplayContent } from './utils/contentUtils';

const HandbookManager = () => {
  const [handbooks, setHandbooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedHandbook, setSelectedHandbook] = useState(null);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [currentLanguage, setCurrentLanguage] = useState(DEFAULT_LANGUAGE);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [tempHandbooks, setTempHandbooks] = useState([]);

  // Fetch handbooks on component mount and when language changes
  useEffect(() => {
    fetchHandbooks();
  }, [currentLanguage]);

  const fetchHandbooks = async () => {
    try {
      setLoading(true);
      const response = await api.get('/handbooks', {
        params: { language: currentLanguage }
      });
      console.log("🤔 ~ :34 ~ fetchHandbooks ~ response:", response)
      const handbooksData = response.data.data || [];
      setHandbooks(handbooksData);
      setTempHandbooks(handbooksData);
    } catch (error) {
      console.error('Error fetching handbooks:', error);
      showSnackbar('Failed to fetch handbooks', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleReorderToggle = () => {
    if (isReorderMode) {
      // Cancel reordering
      setTempHandbooks(handbooks);
      setIsReorderMode(false);
    } else {
      // Start reordering
      setIsReorderMode(true);
    }
  };

  const handleSaveReorder = async () => {
    try {
      console.log('🔄 Starting handbook reorder...');
      const handbookIds = tempHandbooks.map(handbook => handbook.id);
      console.log('📝 Handbook IDs to reorder:', handbookIds);
      
      const response = await api.put('/handbooks/reorder', { handbookIds });
      console.log('✅ Reorder response:', response.data);
      
      setHandbooks(tempHandbooks);
      setIsReorderMode(false);
      showSnackbar('Handbooks reordered successfully', 'success');
    } catch (error) {
      console.error('❌ Error reordering handbooks:', error);
      console.error('❌ Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      
      let errorMessage = 'Failed to reorder handbooks';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Access denied. Admin role required.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      }
      
      showSnackbar(errorMessage, 'error');
    }
  };

  const onDragEnd = (result) => {
    console.log('🔄 Drag ended:', result);
    
    if (!result.destination) {
      console.log('❌ No destination - drag cancelled');
      return;
    }

    if (result.source.index === result.destination.index) {
      console.log('❌ Same position - no change needed');
      return;
    }

    const items = Array.from(tempHandbooks);
    console.log('📋 Before reorder:', items.map(item => ({ id: item.id, title: item.title })));
    
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    console.log('📋 After reorder:', items.map(item => ({ id: item.id, title: item.title })));
    setTempHandbooks(items);
  };

  const handleAddHandbook = async (handbookData) => {
    try {
      const response = await api.post('/handbooks', handbookData);
      showSnackbar('Handbook created successfully', 'success');
      setOpenAddDialog(false);
      fetchHandbooks();
      return response.data.data;
    } catch (error) {
      console.error('Error creating handbook:', error);
      showSnackbar('Failed to create handbook', 'error');
      throw error;
    }
  };

  const handleUpdateHandbook = async (handbookData) => {
    try {
      await api.put(`/handbooks/${selectedHandbook.id}`, handbookData);
      showSnackbar('Handbook updated successfully', 'success');
      setOpenEditDialog(false);
      fetchHandbooks();
    } catch (error) {
      console.error('Error updating handbook:', error);
      showSnackbar('Failed to update handbook', 'error');
    }
  };

  const handleDeleteHandbook = async () => {
    try {
      await api.delete(`/handbooks/${selectedHandbook.id}`);
      showSnackbar('Handbook deleted successfully', 'success');
      setOpenDeleteDialog(false);
      setSelectedHandbook(null);
      fetchHandbooks();
    } catch (error) {
      console.error('Error deleting handbook:', error);
      showSnackbar('Failed to delete handbook', 'error');
    }
  };


  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const openEdit = (handbook) => {
    setSelectedHandbook(handbook);
    setOpenEditDialog(true);
  };

  const openDelete = (handbook) => {
    setSelectedHandbook(handbook);
    setOpenDeleteDialog(true);
  };

  const handleViewHandbook = (handbook) => {
    if (handbook.onlineUrl) {
      window.open(handbook.onlineUrl, '_blank');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          My Handbooks
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="language-select-label">
              <LanguageIcon sx={{ fontSize: 16, mr: 0.5 }} />
              Language
            </InputLabel>
            <Select
              labelId="language-select-label"
              value={currentLanguage}
              label="Language"
              onChange={(e) => setCurrentLanguage(e.target.value)}
            >
              {AVAILABLE_LANGUAGES.map((lang) => (
                <MenuItem key={lang} value={lang}>
                  {lang.charAt(0).toUpperCase() + lang.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {handbooks.length > 1 && (
            <>
              {isReorderMode ? (
                <>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveReorder}
                  >
                    Save Order
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<CancelIcon />}
                    onClick={handleReorderToggle}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<ReorderIcon />}
                  onClick={handleReorderToggle}
                >
                  Reorder
                </Button>
              )}
            </>
          )}
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddDialog(true)}
            disabled={isReorderMode}
          >
            Add Handbook
          </Button>
        </Box>
      </Box>

      {handbooks.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <UploadFileIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No handbooks yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Upload your first handbook to get started
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddDialog(true)}
          >
            Add Your First Handbook
          </Button>
        </Box>
      ) : (
        <DragDropContext 
          onDragEnd={onDragEnd}
          onDragStart={(start) => {
            console.log('🚀 Drag started:', start);
          }}
          onDragUpdate={(update) => {
            console.log('📍 Drag update:', update);
          }}
        >
          <Droppable droppableId="handbooks" isDropDisabled={!isReorderMode}>
            {(provided, snapshot) => (
              <Box 
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{ 
                  display: isReorderMode ? 'flex' : 'grid',
                  flexDirection: isReorderMode ? 'column' : undefined,
                  gridTemplateColumns: isReorderMode ? undefined : {
                    xs: '1fr', // 1 column on mobile
                    sm: 'repeat(2, 1fr)', // 2 columns on tablet
                    md: 'repeat(3, 1fr)', // 3 columns on desktop
                    lg: 'repeat(3, 1fr)', // 3 columns on large desktop
                  },
                  gap: 3,
                  width: '100%'
                }}
              >
                {(isReorderMode ? tempHandbooks : handbooks).map((handbook, index) => (
                  <Draggable 
                    key={handbook.id} 
                    draggableId={String(handbook.id)} 
                    index={index}
                    isDragDisabled={!isReorderMode}
                  >
                    {(provided, snapshot) => (
                      <Card 
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        sx={{ 
                          display: 'flex', 
                          flexDirection: isReorderMode ? 'row' : 'column',
                          height: isReorderMode ? '120px' : '400px',
                          width: isReorderMode ? '100%' : undefined,
                          position: 'relative',
                          border: isReorderMode ? '2px dashed #1976d2' : 'none',
                          backgroundColor: snapshot.isDragging ? '#f5f5f5' : 'background.paper',
                          transform: snapshot.isDragging ? 'rotate(2deg)' : 'none',
                          '&:hover': {
                            boxShadow: 6,
                            transform: isReorderMode ? 'none' : 'translateY(-4px)',
                            transition: 'all 0.3s ease-in-out'
                          }
                        }}
                      >
                        {isReorderMode && (
                          <Box 
                            {...provided.dragHandleProps}
                            sx={{ 
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              zIndex: 1,
                              backgroundColor: 'primary.main',
                              color: 'white',
                              borderRadius: '50%',
                              width: 32,
                              height: 32,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'grab',
                              '&:active': {
                                cursor: 'grabbing'
                              }
                            }}
                          >
                            <DragIndicatorIcon fontSize="small" />
                          </Box>
                        )}
              <CardMedia
                component="img"
                height={isReorderMode ? undefined : "180"}
                image={handbook.coverImage || '/images/default-handbook.png'}
                alt={handbook.title}
                sx={{ 
                  objectFit: 'contain',
                  backgroundColor: 'grey.200',
                  flexShrink: 0,
                  width: isReorderMode ? '120px' : undefined,
                  height: isReorderMode ? '120px' : '180px'
                }}
              />
              <CardContent 
                sx={{ 
                  flexGrow: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  p: 2,
                  overflow: 'hidden',
                  height: isReorderMode ? '120px' : '164px',
                  justifyContent: isReorderMode ? 'center' : undefined
                }}
              >
                <Typography 
                  variant="h6" 
                  component="h2"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    mb: isReorderMode ? 0 : 1,
                    fontSize: isReorderMode ? '1.2rem' : '1.1rem',
                    fontWeight: 600
                  }}
                  title={handbook.title} // Show full title on hover
                >
                  {handbook.title}
                </Typography>
                {!isReorderMode && (
                  <>
                    <Box 
                      sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        flexGrow: 1,
                        mb: 1.5,
                        lineHeight: 1.5,
                        '& p': {
                          margin: '4px 0',
                          fontSize: '0.875rem',
                          color: 'text.secondary'
                        },
                        '& strong, & b': {
                          fontWeight: 600
                        },
                        '& em, & i': {
                          fontStyle: 'italic'
                        },
                        '& ul, & ol': {
                          paddingLeft: '16px',
                          margin: '4px 0'
                        },
                        '& li': {
                          fontSize: '0.875rem',
                          color: 'text.secondary'
                        },
                        '& blockquote': {
                          borderLeft: '3px solid #ddd',
                          paddingLeft: '8px',
                          margin: '4px 0',
                          fontStyle: 'italic',
                          fontSize: '0.875rem',
                          color: 'text.secondary'
                        },
                        '& h1, & h2, & h3, & h4, & h5, & h6': {
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          margin: '4px 0',
                          color: 'text.primary'
                        }
                      }}
                    >
                      {handbook.description ? (
                        <div 
                          dangerouslySetInnerHTML={{ 
                            __html: getWebDisplayContent(getContentPreview(handbook.description, 25))
                          }} 
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No description available
                        </Typography>
                      )}
                    </Box>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        gap: 0.5, 
                        flexWrap: 'wrap',
                        mt: 'auto'
                      }}
                    >
                      {handbook.onlineUrl && (
                        <Chip
                          icon={<LinkIcon sx={{ fontSize: 16 }} />}
                          label="Online"
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ height: 24 }}
                        />
                      )}
                      {handbook.languageSpec && (
                        <Chip
                          icon={<LanguageIcon sx={{ fontSize: 16 }} />}
                          label={`${Object.keys(handbook.languageSpec).length} langs`}
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ height: 24 }}
                        />
                      )}
                    </Box>
                  </>
                )}
              </CardContent>
              {!isReorderMode && (
                <CardActions 
                  sx={{ 
                    justifyContent: 'flex-end',
                    borderTop: 1,
                    borderColor: 'divider',
                    bgcolor: 'grey.50',
                    height: '56px',
                    flexShrink: 0,
                    px: 2
                  }}
                >
                        {handbook.onlineUrl && (
                          <IconButton
                            size="small"
                            onClick={() => handleViewHandbook(handbook)}
                            title="View Handbook"
                            disabled={isReorderMode}
                            sx={{ 
                              '&:hover': { 
                                bgcolor: 'primary.light',
                                color: 'white'
                              }
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => openEdit(handbook)}
                          title="Edit Handbook"
                          disabled={isReorderMode}
                          sx={{ 
                            '&:hover': { 
                              bgcolor: 'info.light',
                              color: 'white'
                            }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => openDelete(handbook)}
                          title="Delete Handbook"
                          color="error"
                          disabled={isReorderMode}
                          sx={{ 
                            '&:hover': { 
                              bgcolor: 'error.light',
                              color: 'white'
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                </CardActions>
              )}
                    </Card>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>
    )}

      {/* Add Handbook Dialog */}
      <Dialog 
        open={openAddDialog} 
        onClose={() => setOpenAddDialog(false)} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: { height: '90vh', maxHeight: 800 }
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <EnhancedHandbookForm
            onSubmit={handleAddHandbook}
            onCancel={() => setOpenAddDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Handbook Dialog */}
      <Dialog 
        open={openEditDialog} 
        onClose={() => setOpenEditDialog(false)} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: { height: '90vh', maxHeight: 800 }
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          {selectedHandbook && (
            <EnhancedHandbookForm
              initialData={selectedHandbook}
              onSubmit={handleUpdateHandbook}
              onCancel={() => setOpenEditDialog(false)}
              isEdit={true}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Delete Handbook</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedHandbook?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteHandbook} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HandbookManager;